<template>
  <section class="orders-history" aria-labelledby="my-orders-title">
    <div class="history-heading">
      <div>
        <h2 id="my-orders-title" class="text-h5 font-weight-medium mb-1">Meine Bestellungen</h2>
        <p class="history-subtitle mb-0">Status, Artikel und Rückfragen an einem Ort.</p>
      </div>
      <v-chip v-if="orders.length > 0" small outlined color="grey lighten-1" class="history-count">
        {{ filteredOrders.length }} von {{ orders.length }}
      </v-chip>
    </div>

    <v-card v-if="orders.length > 0" dark flat class="history-toolbar mb-5">
      <v-text-field
        v-model="searchQuery"
        prepend-inner-icon="mdi-magnify"
        label="Bestellung suchen"
        placeholder="Nummer, Studio oder Artikel"
        outlined
        dense
        dark
        clearable
        hide-details
        class="history-search" />
      <v-select v-model="statusFilter" :items="statusOptions" label="Status" outlined dense dark hide-details class="history-status" />
      <v-tooltip bottom>
        <template v-slot:activator="{ on, attrs }">
          <v-btn
            v-if="hasActiveFilters"
            icon
            large
            color="grey lighten-1"
            v-bind="attrs"
            v-on="on"
            aria-label="Filter zurücksetzen"
            @click="resetFilters">
            <v-icon>mdi-filter-remove-outline</v-icon>
          </v-btn>
        </template>
        <span>Filter zurücksetzen</span>
      </v-tooltip>
    </v-card>

    <div v-if="loading" class="orders-skeletons" aria-live="polite" aria-label="Bestellungen werden geladen">
      <v-skeleton-loader v-for="n in 3" :key="n" dark type="list-item-avatar-three-line" class="order-skeleton" />
    </div>

    <div v-else-if="orders.length === 0" class="empty-state">
      <div class="empty-icon-wrap">
        <v-icon size="42" color="grey lighten-1">mdi-package-variant-closed</v-icon>
      </div>
      <div class="subtitle-1 font-weight-medium mt-4">Noch keine Bestellungen</div>
      <div class="caption grey--text mt-1">Deine erste Bestellung erscheint nach dem Absenden automatisch hier.</div>
    </div>

    <div v-else-if="filteredOrders.length === 0" class="empty-state">
      <div class="empty-icon-wrap">
        <v-icon size="42" color="grey lighten-1">mdi-magnify-close</v-icon>
      </div>
      <div class="subtitle-1 font-weight-medium mt-4">Keine passenden Bestellungen</div>
      <div class="caption grey--text mt-1">Passe die Suche oder den Statusfilter an.</div>
      <v-btn small outlined color="primary" class="mt-4" @click="resetFilters">Filter zurücksetzen</v-btn>
    </div>

    <div v-else class="orders-grid" aria-live="polite">
      <article
        v-for="order in filteredOrders"
        :key="order._id"
        class="order-card"
        role="button"
        tabindex="0"
        :aria-label="orderAriaLabel(order)"
        @click="openOrder(order)"
        @keydown.enter="openOrder(order)"
        @keydown.space.prevent="openOrder(order)">
        <div class="order-card-main">
          <div class="order-header">
            <div class="order-reference">
              <div class="reference-label">Bestellung</div>
              <div class="order-number">{{ displayOrderNumber(order) }}</div>
              <div v-if="order.bestellnummer" class="internal-number">Intern #{{ shortId(order) }}</div>
            </div>
            <v-chip small :color="orderStatusColor(order)" dark class="status-chip">
              <v-icon left x-small>{{ orderStatusIcon(order) }}</v-icon>
              {{ orderStatusLabel(order) }}
            </v-chip>
          </div>

          <div class="order-meta">
            <span><v-icon small color="grey lighten-1">mdi-calendar-blank-outline</v-icon>{{ formatDate(order.createdAt) }}</span>
            <span><v-icon small color="grey lighten-1">mdi-store-outline</v-icon>{{ order.studio ? order.studio.name : "Kein Studio" }}</span>
          </div>

          <div v-if="isDigitalOrder(order)" class="digital-ready-state">
            <v-icon small color="success">mdi-download-circle-outline</v-icon>
            <span>Deine Dateien stehen in den Bestelldetails bereit.</span>
          </div>

          <div v-else class="compact-progress" :aria-label="`Bestellfortschritt: ${statusLabel(order.status)}`">
            <div v-for="(step, idx) in statusSteps" :key="step.value" class="progress-segment-wrap">
              <div
                class="progress-segment"
                :class="{
                  complete: isStepActive(order.status, step.value),
                  current: order.status === step.value,
                }"></div>
              <span v-if="idx === currentStepIndex(order.status)" class="sr-only">Aktueller Status: {{ step.label }}</span>
            </div>
          </div>

          <div v-if="!isDigitalOrder(order)" class="next-step" :class="{ complete: order.status === 'delivered' }">
            <v-icon small :color="order.status === 'delivered' ? 'success' : 'primary'">
              {{ order.status === "delivered" ? "mdi-check-circle-outline" : "mdi-arrow-right-circle-outline" }}
            </v-icon>
            <span>{{ nextStepLabel(order.status) }}</span>
          </div>

          <div class="order-items-preview">
            <span v-for="(item, idx) in order.items.slice(0, 2)" :key="idx" class="item-chip"> {{ item.quantity }}× {{ item.productTitle }} </span>
            <span v-if="order.items.length > 2" class="more-items">+{{ order.items.length - 2 }} weitere</span>
          </div>
        </div>

        <div class="order-card-footer">
          <div class="footer-info">
            <span>{{ totalItems(order) }} Artikel</span>
            <span v-if="order.comments && order.comments.length > 0" class="comment-count">
              <v-icon x-small color="grey lighten-1">mdi-comment-text-multiple-outline</v-icon>
              {{ order.comments.length }}
            </span>
          </div>
          <span class="details-link">Details <v-icon small color="primary">mdi-chevron-right</v-icon></span>
        </div>
      </article>
    </div>
  </section>
</template>

<script>
export default {
  name: "MyOrdersList",
  props: {
    orders: { type: Array, default: () => [] },
    loading: { type: Boolean, default: false },
  },
  data() {
    return {
      searchQuery: "",
      statusFilter: "all",
      statusSteps: [
        {
          value: "received",
          label: "Eingegangen",
          icon: "mdi-inbox-arrow-down",
        },
        { value: "confirmed", label: "Bestätigt", icon: "mdi-check-circle" },
        { value: "shipping", label: "Unterwegs", icon: "mdi-truck-fast" },
        { value: "delivered", label: "Geliefert", icon: "mdi-package-variant" },
      ],
      statusOptions: [
        { text: "Alle Status", value: "all" },
        { text: "Eingegangen", value: "received" },
        { text: "Bestätigt", value: "confirmed" },
        { text: "Unterwegs", value: "shipping" },
        { text: "Geliefert", value: "delivered" },
      ],
    }
  },
  computed: {
    hasActiveFilters() {
      return !!(this.searchQuery && this.searchQuery.trim()) || this.statusFilter !== "all"
    },
    filteredOrders() {
      const query = (this.searchQuery || "").trim().toLowerCase()
      return this.orders.filter((order) => {
        if (this.statusFilter !== "all" && order.status !== this.statusFilter) return false
        if (!query) return true

        const haystack = [
          order._id,
          this.shortId(order),
          order.bestellnummer,
          order.studio && order.studio.name,
          this.statusLabel(order.status),
          ...(order.items || []).map((item) => item.productTitle),
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase()

        return haystack.includes(query)
      })
    },
  },
  methods: {
    openOrder(order) {
      this.$emit("open-detail", order)
    },
    resetFilters() {
      this.searchQuery = ""
      this.statusFilter = "all"
    },
    shortId(order) {
      return order && order._id ? order._id.slice(-6).toUpperCase() : "------"
    },
    displayOrderNumber(order) {
      return order.bestellnummer || `#${this.shortId(order)}`
    },
    formatDate(d) {
      return d ? this.$moment(d).format("DD.MM.YYYY, HH:mm") : "-"
    },
    totalItems(order) {
      return (order.items || []).reduce((sum, item) => sum + (item.quantity || 0), 0)
    },
    isDigitalOrder(order) {
      return !!(order && order.items && order.items.length && order.items.every((item) => item.productType === "digital"))
    },
    orderStatusColor(order) {
      return this.isDigitalOrder(order) ? "green darken-1" : this.statusColor(order.status)
    },
    orderStatusIcon(order) {
      return this.isDigitalOrder(order) ? "mdi-download-circle-outline" : this.statusIcon(order.status)
    },
    orderStatusLabel(order) {
      return this.isDigitalOrder(order) ? "Downloads bereit" : this.statusLabel(order.status)
    },
    statusColor(s) {
      if (s === "delivered") return "green darken-1"
      if (s === "shipping") return "blue darken-1"
      if (s === "confirmed") return "amber darken-3"
      return "blue-grey darken-1"
    },
    statusIcon(s) {
      const step = this.statusSteps.find((item) => item.value === s)
      return step ? step.icon : "mdi-help-circle-outline"
    },
    statusLabel(s) {
      const step = this.statusSteps.find((item) => item.value === s)
      return step ? step.label : "Unbekannt"
    },
    currentStepIndex(s) {
      return this.statusSteps.findIndex((item) => item.value === s)
    },
    isStepActive(currentStatus, stepValue) {
      const currentIndex = this.currentStepIndex(currentStatus)
      const stepIndex = this.currentStepIndex(stepValue)
      return currentIndex >= stepIndex && stepIndex >= 0
    },
    nextStepLabel(status) {
      if (status === "received") return "Als Nächstes wird deine Bestellung geprüft."
      if (status === "confirmed") return "Als Nächstes wird die Bestellung versendet."
      if (status === "shipping") return "Deine Bestellung ist auf dem Weg."
      if (status === "delivered") return "Bestellung abgeschlossen."
      return "Status wird geprüft."
    },
    orderAriaLabel(order) {
      return `${this.displayOrderNumber(order)}, ${this.orderStatusLabel(order)}, ${this.totalItems(order)} Artikel. Details öffnen.`
    },
  },
}
</script>

<style scoped>
.orders-history {
  width: 100%;
}

.history-heading {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 18px;
}

.history-subtitle {
  color: rgba(255, 255, 255, 0.58);
  font-size: 14px;
}

.history-count {
  flex-shrink: 0;
}

.history-toolbar {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px;
  background: rgba(255, 255, 255, 0.045) !important;
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 12px;
}

.history-search {
  flex: 1 1 360px;
}

.history-status {
  flex: 0 1 220px;
}

.orders-grid,
.orders-skeletons {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(360px, 1fr));
  gap: 14px;
}

.order-skeleton {
  border-radius: 12px;
  overflow: hidden;
  background: rgba(255, 255, 255, 0.04);
}

.order-card {
  display: flex;
  flex-direction: column;
  min-width: 0;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.07);
  border-radius: 14px;
  cursor: pointer;
  overflow: hidden;
  transition: transform 180ms ease, border-color 180ms ease, background-color 180ms ease, box-shadow 180ms ease;
}

.order-card:hover,
.order-card:focus-visible {
  background: rgba(255, 255, 255, 0.065);
  border-color: rgba(0, 150, 64, 0.55);
  box-shadow: 0 12px 30px rgba(0, 0, 0, 0.18);
  outline: none;
  transform: translateY(-2px);
}

.order-card-main {
  flex: 1;
  padding: 16px;
}

.order-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
}

.reference-label {
  color: rgba(255, 255, 255, 0.5);
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.order-number {
  color: #fff;
  font-size: 17px;
  font-weight: 700;
  line-height: 1.35;
  word-break: break-word;
}

.internal-number {
  color: rgba(255, 255, 255, 0.42);
  font-size: 11px;
  margin-top: 1px;
}

.status-chip {
  flex-shrink: 0;
}

.order-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 7px 16px;
  color: rgba(255, 255, 255, 0.62);
  font-size: 12px;
  margin-top: 14px;
}

.order-meta span {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  min-width: 0;
}

.compact-progress {
  display: flex;
  gap: 5px;
  margin: 16px 0 10px;
}

.digital-ready-state {
  display: flex;
  align-items: center;
  gap: 7px;
  min-height: 36px;
  margin: 14px 0 10px;
  padding: 8px 10px;
  border-radius: 9px;
  background: rgba(0, 150, 64, 0.1);
  color: #86efac;
  font-size: 12px;
}

.progress-segment-wrap {
  flex: 1;
}

.progress-segment {
  height: 4px;
  background: rgba(255, 255, 255, 0.09);
  border-radius: 999px;
  transition: background-color 180ms ease;
}

.progress-segment.complete {
  background: #009640;
}

.progress-segment.current {
  background: #4ade80;
}

.next-step {
  display: flex;
  align-items: center;
  gap: 7px;
  color: rgba(255, 255, 255, 0.74);
  font-size: 12px;
  min-height: 24px;
}

.next-step.complete {
  color: #86efac;
}

.order-items-preview {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  margin-top: 14px;
}

.item-chip {
  max-width: 100%;
  overflow: hidden;
  padding: 4px 9px;
  background: rgba(255, 255, 255, 0.065);
  border-radius: 999px;
  color: rgba(255, 255, 255, 0.82);
  font-size: 11.5px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.more-items {
  color: rgba(255, 255, 255, 0.48);
  font-size: 11.5px;
}

.order-card-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 44px;
  padding: 9px 14px;
  background: rgba(0, 0, 0, 0.09);
  border-top: 1px solid rgba(255, 255, 255, 0.055);
  font-size: 12px;
}

.footer-info,
.comment-count,
.details-link {
  display: flex;
  align-items: center;
}

.footer-info {
  gap: 12px;
  color: rgba(255, 255, 255, 0.52);
}

.comment-count {
  gap: 4px;
}

.details-link {
  color: #4ade80;
  font-weight: 600;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 280px;
  padding: 42px 20px;
  text-align: center;
  background: rgba(255, 255, 255, 0.025);
  border: 1px dashed rgba(255, 255, 255, 0.1);
  border-radius: 14px;
}

.empty-icon-wrap {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 74px;
  height: 74px;
  background: rgba(255, 255, 255, 0.045);
  border-radius: 50%;
}

.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

@media (max-width: 700px) {
  .history-heading {
    align-items: center;
  }

  .history-heading h2 {
    font-size: 1.25rem !important;
  }

  .history-toolbar {
    flex-wrap: wrap;
  }

  .history-search,
  .history-status {
    flex: 1 1 100%;
  }

  .orders-grid,
  .orders-skeletons {
    grid-template-columns: minmax(0, 1fr);
  }

  .order-card-main {
    padding: 14px;
  }

  .order-card:hover {
    transform: none;
  }
}

@media (max-width: 380px) {
  .order-header {
    align-items: flex-start;
    flex-direction: column;
  }

  .status-chip {
    align-self: flex-start;
  }
}

@media (prefers-reduced-motion: reduce) {
  .order-card,
  .progress-segment {
    transition: none;
  }
}
</style>
